import { useMemo, useState } from 'react';
import { AppTable } from 'components/organisms';
import { commonTexts, settlementTexts } from 'consts/text';
import { parsePaginationDataToTablePaginationData } from 'utils/parsers/table/parsePaginationDataToTablePaginationData';
import { useSearchParamsBasedPagination } from 'hooks/useSearchParamsBasedPagination/useSearchParamsBasedPagination';
import { SettlementSummaryV2SimplePageableDto } from 'types/settlementChildren/settlementChildren';
import { ChildId, ChildSettlementId } from 'types';
import { Option } from 'components/atoms';
import { IconCheck, IconEye, IconRefresh, IconTransferOut } from '@tabler/icons-react';
import { ChildAddEditBillModal } from 'components/templates/settlement/ChildAddBillModal/ChildAddEditBillModal';
import { generatePath, useOutlet } from 'react-router-dom';
import { appPaths } from 'consts/paths/paths';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { useNavigateWithState } from 'hooks/useNavigateWithState/useNavigateWithState';
import { AxiosError } from 'axios';
import { settlementsVerifyErrorParser } from 'utils/errorHandlers/settlement/settlementsVerifyErrorParser';
import { getColumns } from './ChildrenSettlementPage.utils';
import { ChildrenSettlementPageFilters } from './components/ChildrenSettlementPageFilters/ChildrenSettlementPageFilters';
import { useChildrenSettlementPageFilters } from './hooks/useChildrenSettlementPageFilters';
import { useChildrenSettlementPageNetworkManager } from './hooks/useChildrenSettlementPageNetworkManager';

export const ChildrenSettlementPage = () => {
    const outlet = useOutlet();
    const { displaySuccess, displayError } = useSimpleNotification();

    const [addBillModalOpen, setAddBillModalOpen] = useState<ChildId | null>(null);

    const { currentPage, currentPageSize, handlePaginationValuesChange } =
        useSearchParamsBasedPagination();

    const { filtersValues, updateChosenFilter } = useChildrenSettlementPageFilters({
        currentPage,
    });

    const {
        sortData,
        handleSorterChange,
        selectedTime,
        settlementsData,
        isSettlementsDataInitialFetching,
        isSettlementsDataRefetching,
        isDataError,
        groupsData,
        isGroupsDataFetching,
    } = useChildrenSettlementPageNetworkManager({
        filtersValues,
        currentPage,
        currentPageSize,
    });

    const { selectedYear, selectedMonth, selectedDate } = selectedTime;

    const { statefulNavigate } = useNavigateWithState();

    const { mutate: recalculateSettlement, isLoading: isRecalculateSettlementLoading } =
        useAppMutation(StaffHTTPService.settlements.postRecalculateSettlement, {
            key: ['SETTLEMENT_RECALCULATE'],
            onSuccess: () =>
                displaySuccess(
                    settlementTexts.children.recalculate.successfullyRecalculatedSettlementMessage,
                ),
            onError: () => displayError(commonTexts.errorMessages.actionExecution),
            invalidateQueryKeys: [
                ['SETTLEMENTS'],
                ['CHILD_SETTLEMENT'],
                ['CHILD_SETTLEMENT_PAYMENT'],
            ],
        });

    const onRecalculateClick = (record: SettlementSummaryV2SimplePageableDto) => {
        recalculateSettlement({
            childId: record.childInfoBasic.childId as ChildId,
            period: `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}`,
        });
    };

    const { mutate: verifyChildSettlementV2, isLoading: isVerifyChildSettlementV2Loading } =
        useAppMutation(StaffHTTPService.settlements.verifyChildSettlement, {
            key: ['VERIFY_CHILD_SETTLEMENT_V2'],
            onSuccess: () =>
                displaySuccess(
                    settlementTexts.children.verifySettlements.singleSettlement
                        .verifySettlementSuccessMessage,
                ),
            onError: (error: AxiosError) =>
                displayError(
                    settlementsVerifyErrorParser(error, commonTexts.errorMessages.actionExecution),
                ),
            invalidateQueryKeys: [
                ['CHILD_SETTLEMENT_BALANCE'],
                ['SETTLEMENTS'],
                ['CHILD_SETTLEMENT'],
                ['VERIFICATION_STATUS', selectedMonth, selectedYear],
            ],
        });

    const onVerifyClick = async (record: SettlementSummaryV2SimplePageableDto) => {
        try {
            if (record.id) {
                verifyChildSettlementV2(record.id as ChildSettlementId);
            }
        } catch (error) {
            displayError(commonTexts.errorMessages.actionExecution);
        }
    };

    const onAddBillClick = (childId: ChildId) => {
        setAddBillModalOpen(childId);
    };

    const columns = useMemo(() => getColumns(), []);

    const options: Option<SettlementSummaryV2SimplePageableDto>[] = [
        {
            label: settlementTexts.children.actions.billPreview,
            onClick: (record) =>
                statefulNavigate(
                    generatePath(appPaths.app.settlement.children.pickedChild.details.base, {
                        childId: record.childInfoBasic.childId,
                    }),
                ),
            Icon: <IconEye />,
        },
        {
            label: settlementTexts.children.actions.addBill,
            onClick: (record) => onAddBillClick(record.childInfoBasic.childId as ChildId),
            Icon: <IconTransferOut />,
        },
        {
            label: settlementTexts.children.actions.recalculateBill,
            onClick: (record) => onRecalculateClick(record),
            checkIfVisible: (settlementChildDTO: SettlementSummaryV2SimplePageableDto) =>
                !settlementChildDTO.verified,
            Icon: <IconRefresh />,
        },
        {
            label: settlementTexts.children.details.verifyChildSettlement,
            onClick: (record) => onVerifyClick(record),
            checkIfVisible: (settlementChildDTO: SettlementSummaryV2SimplePageableDto) =>
                !settlementChildDTO.verified && !!settlementChildDTO.id,
            isLoading: isVerifyChildSettlementV2Loading,
            Icon: <IconCheck />,
        },
    ];

    return (
        <>
            {outlet}
            <ChildAddEditBillModal
                childId={addBillModalOpen}
                onCancel={() => setAddBillModalOpen(null)}
            />
            <AppTable<SettlementSummaryV2SimplePageableDto>
                isError={isDataError}
                options={options}
                title={settlementTexts.children.tableTitle}
                emptyInfo={settlementTexts.children.emptyInfo}
                columns={columns}
                rowKey={({ childInfoBasic }) => childInfoBasic.childId}
                isDataInitialLoading={isSettlementsDataInitialFetching}
                isDataRefetching={isSettlementsDataRefetching || isRecalculateSettlementLoading}
                dataSource={settlementsData?.content}
                tableManageUtilsProps={{
                    searchInputProps: {
                        defaultValue: filtersValues.searchFilterDebounceValue,
                        onInput: (value) => updateChosenFilter({ type: 'search', value }),
                    },
                    additionalFilters: (
                        <ChildrenSettlementPageFilters
                            sortData={sortData}
                            handleSorterChange={handleSorterChange}
                            groups={groupsData}
                            selectedDate={selectedDate}
                            updateChosenFilter={updateChosenFilter}
                            selectedGroupIds={filtersValues.groupsFilterValue}
                            isGroupsLoading={isGroupsDataFetching}
                        />
                    ),
                }}
                pagination={{
                    ...parsePaginationDataToTablePaginationData({
                        currentPage,
                        currentPageSize,
                    }),
                    total: settlementsData?.totalElements || 0,
                    onChange: handlePaginationValuesChange,
                }}
            />
        </>
    );
};
